import { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { useDisplayStore } from '../../context/DisplayStoreProvider'
import { DEFAULT_TRADE_PATH, useWalletAuth } from '../../context/WalletAuthProvider'
import { FEATURED_WALLETS, EXTRA_WALLETS } from './wallets'
import './LoginModal.css'

const ALL_WALLETS = [...FEATURED_WALLETS, ...EXTRA_WALLETS]
const METAMASK_ANDROID = 'https://play.google.com/store/apps/details?id=io.metamask'

function CloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.41 4.41a.833.833 0 0 1 1.18 0L10 8.822l4.41-4.41a.833.833 0 1 1 1.18 1.178L11.178 10l4.41 4.41a.833.833 0 1 1-1.178 1.18L10 11.177 5.588 15.59a.833.833 0 1 1-1.178-1.178L8.82 10l-4.41-4.41a.833.833 0 0 1 0-1.18Z"
        fill="currentColor"
      />
    </svg>
  )
}

function BackIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.707 4.293a1 1 0 0 1 0 1.414L5.414 11H21a1 1 0 1 1 0 2H5.414l5.293 5.293a1 1 0 0 1-1.414 1.414l-7-7a1 1 0 0 1 0-1.414l7-7a1 1 0 0 1 1.414 0Z"
        fill="currentColor"
      />
    </svg>
  )
}

function InfoIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10 3.25a6.75 6.75 0 1 0 0 13.5 6.75 6.75 0 0 0 0-13.5ZM1.667 10a8.333 8.333 0 1 1 16.666 0 8.333 8.333 0 0 1-16.666 0Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.31 7.044a1.784 1.784 0 0 0-1.133.192 1.513 1.513 0 0 0-.699.783.792.792 0 0 1-1.481-.559c.26-.689.768-1.257 1.42-1.613a3.368 3.368 0 0 1 2.144-.367 3.262 3.262 0 0 1 1.908 1.023c.496.55.775 1.254.774 1.99 0 1.201-.948 1.972-1.587 2.369a6.63 6.63 0 0 1-1.354.635l-.028.01-.008.002h-.003l-.002.001-.235-.756.235.756A.792.792 0 0 1 9.789 10h.001m0 0 .014-.005.06-.021a5.054 5.054 0 0 0 .956-.456c.575-.357.84-.717.84-1.024 0-.332-.125-.66-.367-.928a1.68 1.68 0 0 0-.982-.521M9.265 13.77c0-.438.355-.792.792-.792h.01a.792.792 0 1 1 0 1.584h-.01a.792.792 0 0 1-.792-.792Z"
        fill="currentColor"
      />
    </svg>
  )
}

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.667 2.667a4 4 0 1 0 0 8 4 4 0 0 0 0-8Zm-5.333 4a5.333 5.333 0 1 1 9.546 3.27l3.592 3.592a.667.667 0 0 1-.943.942L9.937 10.88a5.333 5.333 0 0 1-8.604-4.213Z"
        fill="currentColor"
      />
    </svg>
  )
}

function ChevronIcon({ size = 20, className }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.293 19.707a1 1 0 0 1 0-1.414L14.586 12 8.293 5.707a1 1 0 0 1 1.414-1.414l7 7a1 1 0 0 1 0 1.414l-7 7a1 1 0 0 1-1.414 0Z"
        fill="currentColor"
      />
    </svg>
  )
}

function WalletRow({ wallet, disabled, onSelect }) {
  return (
    <button
      type="button"
      data-testid="ListTile"
      onClick={() => onSelect(wallet)}
      disabled={disabled}
      className="login-modal__wallet"
    >
      <img
        data-testid={wallet.iconTestId}
        src={wallet.icon}
        alt={wallet.id}
        className="login-modal__wallet-icon"
      />
      <span className="login-modal__wallet-name">{wallet.name}</span>
      <span className="login-modal__wallet-trailing">
        {wallet.recommended && (
          <span className="login-modal__recommended">Recommended</span>
        )}
        <ChevronIcon size={20} className="login-modal__chevron" />
      </span>
    </button>
  )
}

export default function LoginModal() {
  const { isConnectModalOpen, closeConnectModal } = useDisplayStore()
  const { connect } = useWalletAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [view, setView] = useState('login')
  const [email, setEmail] = useState('')
  const [query, setQuery] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const returnPath = location.state?.returnPath || DEFAULT_TRADE_PATH
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())

  const filteredWallets = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return ALL_WALLETS
    return ALL_WALLETS.filter((wallet) => wallet.name.toLowerCase().includes(needle))
  }, [query])

  const reset = useCallback(() => {
    setView('login')
    setEmail('')
    setQuery('')
  }, [])

  const dismiss = useCallback(() => {
    if (isSubmitting) return
    closeConnectModal()
    reset()
    if (location.pathname === '/connect') {
      navigate(returnPath)
    }
  }, [closeConnectModal, isSubmitting, location.pathname, navigate, reset, returnPath])

  const completeSignIn = async (walletId) => {
    setIsSubmitting(true)
    try {
      await connect(walletId)
      closeConnectModal()
      reset()
      if (location.pathname === '/connect') {
        navigate(returnPath)
      }
    } catch (error) {
      console.error('Connection failed:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    if (!isConnectModalOpen) return undefined
    const onKey = (event) => {
      if (event.key === 'Escape') {
        if (view === 'help') setView('wallets')
        else if (view === 'wallets') setView('login')
        else dismiss()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [dismiss, isConnectModalOpen, view])

  useEffect(() => {
    if (!isConnectModalOpen) return undefined
    const { body, documentElement } = document
    const isDesktop = window.matchMedia('(min-width: 768px)').matches
    const previousOverflow = body.style.overflow
    const previousPadding = body.style.paddingRight
    documentElement.setAttribute('data-connect-open', '')
    if (isDesktop) {
      const scrollbar = window.innerWidth - documentElement.clientWidth
      body.style.overflow = 'hidden'
      if (scrollbar > 0) body.style.paddingRight = `${scrollbar}px`
    }
    return () => {
      documentElement.removeAttribute('data-connect-open')
      body.style.overflow = previousOverflow
      body.style.paddingRight = previousPadding
    }
  }, [isConnectModalOpen])

  if (!isConnectModalOpen) return null

  const metamask = FEATURED_WALLETS.find((wallet) => wallet.id === 'metamask')

  return createPortal(
    <div className="login-modal-root" role="presentation">
      <div className="login-modal-overlay" onClick={dismiss} />
      <div className="login-modal-center">
        <div
          role="dialog"
          aria-labelledby="login-modal-title"
          data-testid="dynamic-auth-modal"
          className={`login-modal login-modal--${view}`}
          onClick={(e) => e.stopPropagation()}
        >
          {view === 'help' ? (
            <>
              <div className="login-modal__header">
                <h2 id="login-modal-title" className="login-modal__title">
                  Get your first wallet
                </h2>
                <button
                  type="button"
                  data-testid="back-button"
                  onClick={() => setView('wallets')}
                  className="login-modal__icon-btn"
                  aria-label="Back"
                >
                  <BackIcon />
                </button>
              </div>
              <div className="login-modal__help">
                <img src={metamask.icon} alt="metamask" className="login-modal__help-icon" />
                <p className="login-modal__help-title">We recommend installing MetaMask</p>
                <p className="login-modal__help-copy">Select your preferred option below:</p>
                <a
                  className="login-modal__help-store"
                  href={METAMASK_ANDROID}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <span className="login-modal__help-store-label">Install Android app</span>
                </a>
                <button
                  type="button"
                  className="login-modal__help-continue"
                  onClick={() => setView('wallets')}
                >
                  Continue once installed
                </button>
              </div>
            </>
          ) : view === 'wallets' ? (
            <>
              <div className="login-modal__header">
                <button
                  type="button"
                  data-testid="back-button"
                  onClick={() => {
                    setQuery('')
                    setView('login')
                  }}
                  className="login-modal__icon-btn"
                  aria-label="Back"
                >
                  <BackIcon />
                </button>
                <h2 id="login-modal-title" className="login-modal__title">
                  Select your wallet
                </h2>
                <button
                  type="button"
                  data-testid="info-button"
                  onClick={() => setView('help')}
                  className="login-modal__icon-btn"
                  aria-label="Help"
                >
                  <InfoIcon />
                </button>
              </div>
              <div className="login-modal__search-wrap">
                <label className="login-modal__search">
                  <span className="login-modal__search-icon">
                    <SearchIcon />
                  </span>
                  <input
                    data-testid="Search"
                    className="login-modal__search-input"
                    placeholder="Search through 586 wallets..."
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                  />
                </label>
              </div>
              <div className="login-modal__list" data-testid="wallet-list-scroll-container">
                {filteredWallets.map((wallet) => (
                  <WalletRow
                    key={wallet.id}
                    wallet={wallet}
                    disabled={isSubmitting}
                    onSelect={(item) => completeSignIn(item.id)}
                  />
                ))}
                <div className="login-modal__missing">
                  <p>Don&apos;t see your wallet?</p>
                  <p>Try search instead</p>
                </div>
              </div>
              <div className="login-modal__footer" data-testid="default-footer" />
            </>
          ) : (
            <>
              <div className="login-modal__header">
                <h2 id="login-modal-title" className="login-modal__title">
                  Log in or sign up
                </h2>
                <button
                  type="button"
                  data-testid="close-button"
                  onClick={dismiss}
                  className="login-modal__icon-btn"
                  aria-label="Close"
                >
                  <CloseIcon />
                </button>
              </div>
              <div className="login-modal__body" data-testid="login-view-scroll">
                <form
                  className="login-modal__email"
                  data-testid="email-form"
                  onSubmit={(event) => event.preventDefault()}
                >
                  <div className="login-modal__field">
                    <input
                      id="email_field"
                      className="login-modal__input"
                      type="email"
                      autoComplete="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                    />
                    <label htmlFor="email_field" className="login-modal__label">
                      Enter your email
                    </label>
                  </div>
                  <button
                    type="submit"
                    className="login-modal__continue"
                    data-testid="submit_button"
                    disabled={!emailValid}
                  >
                    Continue
                  </button>
                </form>
                <div className="login-modal__divider" aria-hidden="true">
                  <span />
                  <span />
                </div>
                <div className="login-modal__wallets" data-testid="top-wallets-list">
                  {FEATURED_WALLETS.map((wallet) => (
                    <WalletRow
                      key={wallet.id}
                      wallet={wallet}
                      disabled={isSubmitting}
                      onSelect={(item) => completeSignIn(item.id)}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={() => setView('wallets')}
                    disabled={isSubmitting}
                    className="login-modal__all"
                  >
                    <span className="login-modal__all-label">View all wallets</span>
                    <ChevronIcon size={12} className="login-modal__all-chevron" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
